import React, { useEffect, useMemo, useState, useRef, Suspense, lazy } from 'react'
import { useLazyGetProductReviewQuery, useDeleteReviewMutation, useUpdateReviewMutation } from "context/review/reviewApiSlice";
import { nanoid } from '@reduxjs/toolkit';
import { formatDistanceToNow, parseISO } from 'date-fns';
import frLocale from 'date-fns/locale/fr';

import MDAvatar from "dashboard/components/MDAvatar";
import MDAlert from "dashboard/components/MDAlert";
import MDBox from 'dashboard/components/MDBox';
import MDTypography from "dashboard/components/MDTypography";
import {  Grid,  Container, Divider, Box, IconButton, Icon, Tooltip } from "@mui/material";
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';

import ScrollDownBtn from "components/sections/product/components/ScrollDownBtn";
import useSearch from "hooks/useSearch";
import useAlert from "hooks/useAlert";
import useEmoji from "hooks/useEmoji";
import Reactions from 'components/sections/product/components/Reactions';
// import UserRate from "components/Home/products/components/UserRate"

const NewMessageForm = lazy(() => import('components/sections/product/components/NewMessageForm'));
const importMessageMenu = () => lazy(() =>import('components/sections/product/components/MessageMenu'));

const StackP = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.caption,
  boxShadow :  theme.boxShadows.sm ,
  padding: 2,
  textAlign: 'center',
  color: theme.palette.text.main,
  borderRadius : 20,
  '& .MuiAvatar-root.MuiAvatar-circular' : {
    height : '18px !important',
    width : '18px !important'
  },
  lineHeight:"12px"
}));
/* eslint-disable */
const CustomeListItem = styled(ListItem)(({ theme, isonlyemoji }) => ({
  '& .MuiListItemText-root' : {
    background: isonlyemoji === 1 ? "transparent" 
    : theme.palette.background.default,
    borderRadius : theme.borders.borderRadius.xxl,
    boxShadow :  isonlyemoji === 1 ? "none" :  theme.boxShadows.sm ,
    minHeight:'40px',
    maxWidth : "65%",
    width : "auto",
    position :"relative",
    flex:"inherit",
    paddingRight:20,
    },
    '& .MuiTypography-root':{
      color: theme.palette.text.main ,
      '& b' : {
        fontWeight : 600,
        color : theme.palette.primary.main
      }
    },
   '& .stack-reaction':{
    left :  "calc(100% - 15px)",
    top: "50%",
    transform :"translateY(-50%)",
    borderRadius : theme.borders.borderRadius.lg,
   },
   '& .min-line-height span': {
    lineHeight:".8rem !important",
    paddingRight : '0 !important' 
   },
   '& .MuiListItemIcon-root' : {
      alignSelf:"flex-start",
   },
   '& .interact-with-review .MuiTypography-button' : {
     marginRight :'1rem', 
     cursor : "pointer",
     textAlign:"center",
     fontWeight : theme.typography.fontWeightMedium,
     color:`${theme.palette.text.main} !important`, 
     lineHeight:"1em !important", 
     '&:hover':{color:`${theme.palette.primary.main} !important`}
   },
  ...theme.typography.body2,
  padding: theme.spacing(.1),
  textAlign: 'center',
  color: theme.palette.text.main,
  marginBottom: 0 
}));

const CostumeReview = ({review,connectedUser, children,  replayToReview, replayToReviewFunc ,darkMode,  reactToReview, listChild})=>{
  const { replaceEmiji, isOnlyEmojis, loaderAnimation } = useEmoji();
  const [messageMenus, setMessageMenus] = useState({});
  const [readyToUpdate, setReadyToUpdate]  = useState(null)  
  const { showAlerts } = useAlert();

  const handleCloseMenu = message => {
    setMessageMenus(c => ({ ...c, [message] : {...c[message], visible: false }  }))
  };
  
  const [updateReview, {isLoading : updateLoading}] = useUpdateReviewMutation();
  const [deleteReview, {isLoading : deleteLoading}] = useDeleteReviewMutation();

  const handleUpdate =  async( id, update) => {
      try{
        /*eslint-disable*/
        const updateMessageResponse = await updateReview({filter :id, update}).unwrap()
        showAlerts({code:"success", icon:"done", title : "Review update",msg : updateMessageResponse.message, type : "toast"});
      }catch(error){
        showAlerts({code:"error", icon:"error", title : "Review error",msg : error.data.error, type : "toast"});
      }   
  };

  const handleDelete =  async id => {
    try{
      /*eslint-disable*/
      const deleteMessageResponse = await deleteReview({id:id}).unwrap()
      showAlerts({code:"success", icon:"done", title : "Review Deleting",msg : deleteMessageResponse.message, type : "toast"});
    }catch(error){
      showAlerts({code:"error", icon:"error", title : "Review Deleting",msg : error.data.error, type : "toast"});
    }   
  };

  const loadMessageMenu = (event,message) => {
    if (messageMenus[message]) {
      setMessageMenus(c => ({ ...c, [message]: {...c[message], visible: true }  }));
      return;
    }
    const MessageMenu = importMessageMenu();
    const target = event.currentTarget;
    setMessageMenus(c => ({ ...c, [message]: {menu : <MessageMenu nanoid={nanoid} delete={handleDelete} update={setReadyToUpdate} openMenu={target} handleCloseMenu={handleCloseMenu}  connectedUser={connectedUser} id={message} />, visible: true}  }));
  };

  return (
        readyToUpdate === review.id ? (
                <CustomeListItem sx={{alignItems : "center "}} key={nanoid()}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <ListItemIcon sx={{minWidth : 28, width : 28, display:"flex", alignSelf:"center !important"}}>
                    <Tooltip title="Cancel update">
                      <IconButton
                        size="small"
                        disableRipple
                        color="inherit"
                        aria-controls="notification-menu"
                        aria-haspopup="true"
                        variant="contained"
                        onClick={() => setReadyToUpdate(null)}
                      >
                        <Icon sx={{ color : ({ palette: { text }})=> text , ml:1}} fontSize="medium" >arrow_back_ios</Icon>
                      </IconButton>
                    </Tooltip>
                    </ListItemIcon>
                    <Grid flexGrow={1} item>
                      <NewMessageForm id={review.id} update={handleUpdate} content={review.content} updateIsloading={updateLoading} replayToReviewUser={replayToReview}  sx={{backgroundColor:"none", width:"100%"}} type="commentaire" darkMode={darkMode}  chatBox={review.parent} receipId={review.product} />
                    </Grid>
                </Suspense>
                </CustomeListItem>) :
            (
            <React.Fragment key={nanoid()}>
              <CustomeListItem 
                onMouseEnter={e =>loadMessageMenu(e, review.id)} 
                ref={ele => listChild.current[review.id] = ele} isonlyemoji={isOnlyEmojis(review.content)} 
                disablePadding 
                >
                <ListItemIcon
                    sx={{ 
                        minWidth: 0,
                        justifyContent: 'center',
                        color:"inherit",
                        mr:1
                    }}
                    >
                <MDAvatar   src={`${process.env.REACT_APP_PUBLIC_URL}/img/profil/${review.user.photoURL}`}  alt="profile-image" size="sm" shadow="sm" />
                </ListItemIcon>   
                <ListItemText 
                  sx={{p:1}}> 
                    <MDTypography textAlign="left"  lineHeight={1.2}  variant="h6">{review.user.displayName}</MDTypography>            
                    <MDTypography lineHeight={1.2} variant="body2" sx={{textAlign:"left"}}>{replaceEmiji(review.content)}</MDTypography>
                    {review.reaction?.length > 0 && <StackP direction="row" className="stack-reaction" sx={{position:"absolute"}}>
                      {review.reaction.map( item =>(
                        <Box key={nanoid()}>
                          <MDAvatar sx={{ m:.1, height:18, width:18 }} src={`${process.env.REACT_APP_PUBLIC_URL}/img/reactions/${item.content}.gif`}  alt={item.content}  />
                        </Box>
                      ))}
                      {review.totalReactions > 1 && <MDTypography sx={{fontWeight:600, lineHeight:1.5}} variant="caption"> {review.totalReactions}</MDTypography>}
                    </StackP>}   
                </ListItemText>
                <Suspense fallback={loaderAnimation}>
                  {messageMenus[review.id]?.visible && messageMenus[review.id].menu}
                </Suspense>
              </CustomeListItem>
              <CustomeListItem  sx={{mb:1.2, mt:.5}}>
                <ListItemIcon
                    sx={{minWidth : 45}}
                   />
                <MDBox className="interact-with-review" sx={{display: "flex", alignItems: "center",justifyContent: "space-around"}}>   
                  <MDTypography onClick={() =>reactToReview(review.id)} variant="button"   >Like</MDTypography>
                  <MDTypography variant="button" onClick={()=>replayToReviewFunc(review.parent === null ? review.id : review.parent,review.user.displayName) }>Répondre</MDTypography>
                  <MDTypography sx={{ lineHeight:"1em !important"}} variant="caption">{formatDistanceToNow(parseISO(review.createdAt),{locale: frLocale})}</MDTypography>
                </MDBox>
              </CustomeListItem>
             {children}
             {connectedUser && replayToReview?.id === review.id  && replayToReview.username !== '' &&  review.parent === null &&  (<CustomeListItem  sx={{mb:1.2, mt:.5}}>
                  <Suspense fallback={<div>Loading...</div>}>
                    <ListItemIcon
                        sx={{minWidth : 45}}
                      />
                    <Grid flexGrow={1} item>
                      <NewMessageForm replayToReviewUser={replayToReview}  sx={{backgroundColor:"none", width:"100%"}} type="review" darkMode={darkMode}  chatBox={review.id} receipId={review.product} />
                    </Grid>
                  </Suspense>
                </CustomeListItem>)}
          </React.Fragment>
          )
)}
const TreeReviws = ({items, connectedUser, replayToReview, replayToReviewFunc, reactToReview , darkMode, listChild}) =>{
  return    (items?.map((review, index) =>{ 
            const sortedArray = [...review.related_reviews].sort((a, b) => parseISO(a.createdAt) - parseISO(b.createdAt));
            return  review.related_reviews?.length !== 0 ? (
              <CostumeReview connectedUser={connectedUser} darkMode={darkMode}  reactToReview={reactToReview}  replayToReviewFunc={replayToReviewFunc} replayToReview={replayToReview} listChild={listChild}  key={nanoid()}  review={review} >            
                <List sx={{pl:2, '& .MuiAvatar-root':{height:30, width:30}}} >
                  <TreeReviws connectedUser={connectedUser} darkMode={darkMode}  reactToReview={reactToReview} listChild={listChild} replayToReviewFunc={replayToReviewFunc} replayToReview={replayToReview} items={sortedArray} />
                </List>
            </CostumeReview>) : (
                <CostumeReview connectedUser={connectedUser} darkMode={darkMode}  reactToReview={reactToReview}  listChild={listChild} replayToReviewFunc={replayToReviewFunc} replayToReview={replayToReview} key={nanoid()}  review={review} />
            )
            })
        )
}

const Reviewbox = ({ productID, connectedUser, darkMode, ratingStars, alreadyRated, rating, totalRatingNumber}) => {

  const [noMoreResults, setNoMoreResults] = useState(0);
  const [reviewsBody, setReviewsBody] = useState([]);
  const [replayToReview, setReplayToReview] = useState({id:'', username :'', user : connectedUser, position:{x:0,y:0}});
  const reviewsEndRef = useRef(null);
  const listChild = useRef([]);
  const listInnerRef = useRef(0);
  const scrollBtn = useRef(null);
  const { showAlerts } = useAlert();
  const { searchTerm, filterSearch } = useSearch();
  const { loaderAnimation } = useEmoji();
  
  let mounted;
  useEffect( () => {
    mounted =  true;
    return ()=>{
      if(mounted){
        setReviewsBody([]);
        filterSearch({...searchTerm, page : 0 }); 
        setReplayToReview(null)
      }
      mounted = false;
    }
  }, [productID]);

  const reviewsQuery = useMemo( ()=>{
      const filter = Object.fromEntries(Object.entries(searchTerm.filters).filter((item,key) => item[1] !== null && item[1] !== undefined && item[1] !== '' )); 
      return ({
        page: searchTerm.page,
        filters: JSON.stringify({...filter,
          product : productID
        }),
        reviewsPerpage: 20,
        sortBy: "dateDESC"}
        )},
      [searchTerm,productID]
    );

  const [getReviews,{
      data : reviews = [],
      isLoading,
      isSuccess,
      isFetching,
      isError,
      error }] = useLazyGetProductReviewQuery(reviewsQuery, {
      // pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
      skip: noMoreResults === 0 || reviewsQuery === undefined
  });
  
  const scroolParentToView = () => {
    listInnerRef.current?.lastElementChild?.scrollIntoView({ behavior: "smooth", block: 'end' });
  }
  
  useEffect(()=>{
    if (isSuccess) {
      const { entities , total_results } = reviews;
      const  result  = entities ? Object.values(entities).sort((a, b) => parseISO(a.createdAt) - parseISO(b.createdAt)) : []; 
      setReviewsBody(result);
      setNoMoreResults(total_results);
      const { scrollTop, scrollHeight } = listInnerRef.current;
      if( searchTerm.page === 0 && result.length !== 1 && replayToReview?.id === '') {
        setTimeout(()=> scroolParentToView(),300);
      }else if( result.length === 1 ) {
        if(scrollHeight - scrollTop < 1100){
          scroolParentToView();
        }else{ 
          scrollBtn.current.style.display = "block";
        }
      }
      return () => {}
  }
  },[reviews]);

  useEffect(()=>getReviews(reviewsQuery),[reviewsQuery])

  useEffect(() => {
    if(!productID?.id) {
        setNoMoreResults(0);
    }
    /* eslint-disable */
    isError ? showAlerts({
        title : "Review Alert",
        type  : "toast",
        msg :  error.data?.error ,
        color :"error"
    }) : null
  }, [isError]);

  const onScroll = () => {
    if (listInnerRef.current) {

      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      
      if (scrollTop === 0 && noMoreResults !== 0 && !(isFetching || isLoading) ) { // if (scrollTop + clientHeight === scrollHeight) {
        // This will be triggered after hitting the last element.
        // API call should be made here while implementing pagination.
        scrollTopFetch(searchTerm.page)
      }
      if( scrollHeight - scrollTop < 1100 ) {
        scrollBtn.current.style.display = "none";
      }
    }
  };

  const scrollTopFetch = page => {
    filterSearch({...searchTerm, page : page + 1});
  }

  const replayToReviewFunc = (id, username) => {
    if(!connectedUser) {
      showAlerts({
        title : "Review Alert",
        type  : "toast",
        msg :  "You have to login to replay to this comment",
        color :"info"
     })
      return
    }
   
    setReplayToReview({id,username : `@${username} `, user : connectedUser});
    listChild.current[id].scrollIntoView({ behavior: "smooth" });
  };
  
  const getPosition = element => {
    let x = element.parentElement.offsetLeft;
    let y = element.offsetTop + listInnerRef.current.offsetTop + Math.ceil(element.offsetHeight /2)  - 70;
    if(!element.parentElement.classList.contains("main-list-review")) {
       y += element.parentElement.offsetTop 
       x += element.parentElement.offsetLeft
    }
    return {x,y}
  };

  const reactToReview    = id => {
    if(!connectedUser) {
      showAlerts({
        title : "Review Alert",
        type  : "toast",
        msg :  "You have to login to React to this comment",
        color :"info"
     })
      return
    }
    const {x,y} = getPosition(listChild.current[id]);
    setReplayToReview({id , username : '', position:{x,y}, user : connectedUser});
};
  

  return (
      <Container sx={{p:0}}>
        <MDTypography mt={2} variant="h2">Avis des clients</MDTypography>
        {reviewsBody?.length === 0 && <MDAlert sx={{fontSize : ({typography : {body2}})=>body2, py:.5, minHeight:40}} color="light">Aucun avis Pour l'instant !</MDAlert>}
        <List className="main-list-review" onScroll={onScroll} ref={listInnerRef}
          sx={{color:({palette:{text}})=>text.main,  maxHeight:"100vh", p:{xs:.5,lg:2}, mt:2, overFlowY:"scroll", overflowY:"auto", overflowX:'hidden'}}>
            {( isLoading || isFetching ) && loaderAnimation}       
            {reviewsBody?.length !== 0 ? 
            <TreeReviws 
              listChild={listChild} 
              reactToReview={reactToReview}
              replayToReviewFunc={replayToReviewFunc}
              replayToReview={replayToReview}
              items={reviewsBody}
              connectedUser={connectedUser}
              />: null}
            <ScrollDownBtn key={nanoid()} ref={scrollBtn}  scroolParentToView={scroolParentToView} />
            <div ref={reviewsEndRef}  />
            <Reactions key={nanoid()} showAlerts={showAlerts} setReplayToReview={setReplayToReview} reactTo={replayToReview}   />
        </List>
        <Divider sx={{my:connectedUser ? 0.5 : 1}} />
        {connectedUser ? (<Grid container>
          <Grid sx={{ width:38 }} item>
            <MDAvatar sx={{mr:"0 !important",mt:1}}  src={`${process.env.REACT_APP_PUBLIC_URL}/img/profil/${connectedUser.photoURL}`} alt="profile-image" size="sm" shadow="sm" />
          </Grid>
          <Grid flexGrow={1} item>
            <Suspense fallback={<div>Loading...</div>}>
              <NewMessageForm  replayToReviewUser={{user:connectedUser}} sx={{backgroundColor:"none"}} type="review" darkMode={darkMode} cahtBox={null} receipId={productID} />
            </Suspense>
          </Grid>
        </Grid>) : (
          <MDTypography color="warning" variant="body2">You have to login to post a comment</MDTypography>
        )}
      </Container>
  )
}

export default Reviewbox